import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { LEGAL_CONTENT, LEGAL_LINKS, LOGO } from "../data/content";

export default function Legal() {
  const { slug } = useParams();
  const doc = LEGAL_CONTENT[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#F9FAF8]" data-testid="legal-page">
      <header className="border-b border-black/5 bg-white/80 backdrop-blur-xl">
        <div className="max-w-[900px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="legal-logo-link" className="flex items-center gap-3">
            <img src={LOGO} alt="Fontaneros en Huelva" className="h-9 w-9 object-contain" />
            <span className="font-serif-display text-lg font-semibold text-[#0A1F10]">Fontaneros en Huelva</span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm font-bold text-[#5AB82D] hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-5 md:px-8 py-16 md:py-24">
        {doc ? (
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5AB82D]">Legal</span>
            <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A1F10] leading-[1.05]">
              {doc.title}
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#4B5E51] leading-relaxed">{doc.intro}</p>

            <div className="mt-12 space-y-10">
              {doc.sections.map((s, i) => (
                <section key={i} className="border-t border-black/10 pt-8">
                  <h2 className="font-serif-display text-2xl font-semibold text-[#0A1F10] mb-3">{s.h}</h2>
                  {s.list ? (
                    <dl className="divide-y divide-black/5">
                      {s.list.map((item, j) => (
                        <div key={j} className="flex flex-col sm:flex-row sm:gap-4 py-2.5">
                          <dt className="text-sm font-bold text-[#0A1F10] sm:w-52 shrink-0">{item.label}</dt>
                          <dd className="text-base text-[#4B5E51] break-words">{item.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="text-base text-[#4B5E51] leading-relaxed">{s.p}</p>
                  )}
                </section>
              ))}
            </div>

            <div className="mt-16 pt-8 border-t border-black/10">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5AB82D] mb-4">Otros documentos legales</p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {LEGAL_LINKS.filter((l) => l.slug !== slug).map((l) => (
                  <Link key={l.slug} to={`/legal/${l.slug}`} className="text-sm font-semibold text-[#5AB82D] hover:underline">
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </motion.article>
        ) : (
          <div className="text-center py-20">
            <h1 className="font-serif-display text-3xl font-semibold text-[#0A1F10]">Documento no encontrado</h1>
            <Link to="/" className="mt-4 inline-block text-sm font-bold text-[#5AB82D] hover:underline">
              Volver al inicio
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
